import { formatUsdValue } from '@chainflip/utils/number';
import { subDays } from 'date-fns';
import { Card, FancyDollarDisplay, SkeletonLine } from '@/shared/components';
import { GetLpEarnedFeeAggregates } from '@/shared/graphql/lp/lp';
import { useLpStats } from '@/shared/hooks';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import {
  BoltIcon,
  ChartIcon,
  LayersIcon,
  MoneyBagIcon,
  TrendingUpIcon,
} from '@/shared/icons/small';
import { getDateTruncatedToHour } from '@/shared/utils';

export const LPPortfolioCards = () => {
  const { selectedAccount } = usePolkadot();
  const skeletonLineWidth = 100;

  const {
    totalCollectedFeesUsd,
    deployedLiquidityUsd,
    boostLiquidityUsd,
    totalLiquidityUsd,
    liquidityDataLoading,
    boostFeesLoading,
  } = useLpStats(selectedAccount?.idSs58);

  const boostShareGroupBy = 'EXECUTED_AT_TIMESTAMP_TRUNCATED_TO_DAY';
  const orderGroupBy = 'BLOCK_TIMESTAMP_TRUNCATED_TO_DAY';
  const startDate = subDays(new Date(), 1);
  const endDate = new Date();

  const { data: response, isLoading: fees24hLoading } = useGqlQuery(GetLpEarnedFeeAggregates, {
    context: { clientName: 'lpProcessor' },
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      idSs58: selectedAccount?.idSs58!,
      minTimestamp: getDateTruncatedToHour(startDate).toISOString(),
      maxTimestamp: getDateTruncatedToHour(endDate).toISOString(),
      boostShareGroupBy,
      limitOrderGroupBy: orderGroupBy,
      rangeOrderGroupBy: orderGroupBy,
    },
    enabled: Boolean(selectedAccount?.idSs58),
    refetchInterval: 60_000,
  });

  const fees24h =
    Number(
      response?.account?.boostFees.groupedAggregates?.reduce(
        (acc, val) => acc + Number(val.sum?.feeUsd ?? 0),
        0,
      ) ?? 0,
    ) +
    Number(
      response?.account?.limitOrderFees.groupedAggregates?.reduce(
        (acc, val) => acc + Number(val.sum?.feeUsd ?? 0),
        0,
      ) ?? 0,
    ) +
    Number(
      response?.account?.rangeOrderFees.groupedAggregates?.reduce(
        (acc, val) => acc + Number(val.sum?.baseFeeUsd ?? 0) + Number(val.sum?.quoteFeeUsd ?? 0),
        0,
      ) ?? 0,
    );

  return (
    <>
      <Card
        skeletonLineWidth={skeletonLineWidth}
        loading={boostFeesLoading || liquidityDataLoading}
        icon={<MoneyBagIcon className="text-cf-light-2" />}
        title="Fees"
        tooltip="The combined USD value of fees from both liquidity provision and boost positions across all pools since the beginning"
        footer={
          fees24hLoading ? (
            <SkeletonLine width={120} />
          ) : (
            fees24h !== 0 && (
              <div className="flex items-center gap-x-1 font-aeonikRegular text-12">
                <div className="flex items-center gap-x-0.5 text-cf-green-3">
                  <TrendingUpIcon />
                  +${fees24h.toFixed(2)}
                </div>
                <span className="text-cf-light-2">Last 24H</span>
              </div>
            )
          )
        }
      >
        <FancyDollarDisplay value={formatUsdValue(totalCollectedFeesUsd)} />
      </Card>
      <Card
        skeletonLineWidth={skeletonLineWidth}
        loading={liquidityDataLoading}
        icon={<LayersIcon className="text-cf-light-2" />}
        title="Open Orders"
        tooltip="The cumulative USD value of your open orders across all pools"
        footer="Current"
      >
        <FancyDollarDisplay value={formatUsdValue(deployedLiquidityUsd)} />
      </Card>
      <Card
        skeletonLineWidth={skeletonLineWidth}
        loading={liquidityDataLoading}
        icon={<BoltIcon className="text-cf-light-2" />}
        title="Boost Positions"
        tooltip="The cumulative USD value of your open boost pool positions across all pools"
        footer="Current"
      >
        <FancyDollarDisplay value={formatUsdValue(boostLiquidityUsd)} />
      </Card>
      <Card
        skeletonLineWidth={skeletonLineWidth}
        loading={liquidityDataLoading}
        icon={<ChartIcon className="text-cf-light-2" />}
        title="Portfolio Value"
        tooltip="The combined USD value of your balance and all open positions across all pools"
        footer="Current"
      >
        <FancyDollarDisplay value={formatUsdValue(totalLiquidityUsd)} />
      </Card>
    </>
  );
};
